import 'moment/locale/en-gb';
import 'moment/locale/pl';
import { Column } from "primereact/column";
import { Dropdown } from 'primereact/dropdown';
import { classNames } from 'primereact/utils';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import { ReactComponent as ArrowDownIcon } from '../../assets/images/arrow_down.svg';
import { ReactComponent as ArrowUpIcon } from '../../assets/images/arrow_up.svg';
import { ReactComponent as LocationIcon } from '../../assets/images/location-icon.svg';

import DataTable, {
    FILTER_TYPES,
    DataTableContext,
    DataTableProvider,
} from "../../component/datatable2";

import PeriodSelector from '../../component/periodselector/PeriodSelector';
import { MessageContext } from '../../context/MessageContext';
import { useDashboardData } from '../../context/DashboardContext';
import { TransactionService } from '../../service/TransactionService';
import { FormattedUtils } from '../../utils/FormattedUtils';
import './SalesByLocationWidget.scss';
import './Widget.scss';

const SalesByLocationWidget = () => {
    return (
        <DataTableProvider>
            <SalesByLocationWidgetImpl />
        </DataTableProvider>
    );
};

const SalesByLocationToolbar = ({
    rowsPerPageOptions,
    defaultSearchCriteria,
    period,
    setPeriod,
    isCollapsed,
    toggleCollapse,
}) => {
    const { searchCriteria, setSearchCriteria } = useContext(DataTableContext);

    return (
        <div className="flex align-items-center gap-2">
            {!isCollapsed && (
                <>
                    <Dropdown
                        className="small"
                        id="rowsPerPage"
                        options={rowsPerPageOptions}
                        value={searchCriteria?.rows ?? defaultSearchCriteria.rows}
                        onChange={e =>
                            setSearchCriteria(prev => ({
                                ...prev,
                                first: 0,
                                rows: e.value,
                                timestamp: Date.now(),
                            }))
                        }
                    />

                    <PeriodSelector
                        id="period"
                        value={period}
                        onChange={period => {
                            if (period?.from && period?.to && period?.periodType) {
                                setPeriod(period);

                                setSearchCriteria(prev => ({
                                    ...prev,
                                    first: 0,
                                    timestamp: Date.now(),
                                }));
                            }
                        }}
                        defaultFilterType="currentWeek"
                    />
                </>
            )}

            {isCollapsed ? (
                <ArrowUpIcon className="collapse-button" onClick={toggleCollapse} />
            ) : (
                <ArrowDownIcon className="collapse-button" onClick={toggleCollapse} />
            )}
        </div>
    );
};

const SalesByLocationWidgetImpl = () => {

    const { t, i18n } = useTranslation();
    const isMounted = useRef(false);
    const rowsPerPageOptions = [5, 10, 20, 50];
    const { showErrorMessage } = useContext(MessageContext);
    const { searchCriteria, setSearchCriteria } = useContext(DataTableContext);
    const { locationOptions, cityOptions } = useDashboardData();
    const [maxTransactionCount, setMaxTransactionCount] = useState(0);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [period, setPeriod] = useState();

    const defaultSearchCriteria = useMemo(() => ({
        first: 0,
        rows: 5,
        sortField: 'revenue',
        sortOrder: -1,
        baseQuery: '',
        filters: {}
    }), []);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (isMounted.current && period && period.from && period.to) {
            setSearchCriteria(prev => ({ ...prev }));
            fetchMaxTransactionCount();
        }
    }, [period]);


    const filterConfig = useMemo(() => [
        {
            fieldName: 'location',
            label: t('salesByLocation.location'),
            type: FILTER_TYPES.LIST,
            params: locationOptions,
            apiParamName: 'locations',
            apiValueConverter: value => value.map(r => r.id).join(',')
        },
        {
            fieldName: 'city',
            label: t('salesByLocation.city'),
            type: FILTER_TYPES.LIST,
            params: cityOptions,
            apiParamName: 'cities',
            apiValueConverter: value => value.map(r => r.id).join(',')
        },
        {
            fieldName: 'transactionCount',
            label: t('salesByLocation.transactionCount'),
            type: FILTER_TYPES.NUMBER_RANGE,
            apiParamName: 'transaction_count',
            params: { maxValue: maxTransactionCount }
        },
        {
            fieldName: 'revenue',
            label: t('salesByLocation.revenue'),
            type: FILTER_TYPES.AMOUNT,
            apiParamName: 'revenue',
            params: {
                maxValue: 999999,
                minValue: 0
            }
        },
        {
            fieldName: 'growth',
            label: t('salesByLocation.growth'),
            type: FILTER_TYPES.AMOUNT,
            apiParamName: 'growth',
            params: {
                maxValue: 10000,
                minValue: -10000
            }
        },
    ], [locationOptions, cityOptions, maxTransactionCount]);

    const normalizeCriteria = (criteria) => ({
        ...defaultSearchCriteria,
        ...criteria,
        first: criteria?.first ?? defaultSearchCriteria.first,
        rows: criteria?.rows ?? defaultSearchCriteria.rows,
        sortField:
            !criteria?.sortField || criteria.sortField === 'null'
                ? defaultSearchCriteria.sortField
                : criteria.sortField,
        sortOrder:
            criteria?.sortOrder === null || criteria?.sortOrder === undefined
                ? defaultSearchCriteria.sortOrder
                : criteria.sortOrder,
        baseQuery: criteria?.baseQuery ?? defaultSearchCriteria.baseQuery,
        filters: criteria?.filters ?? defaultSearchCriteria.filters,
    });


    const fetchData = async (criteria) => {
        try {
            if (!period) {
                return { data: [], totalRecords: 0 };
            }

            return await TransactionService.getTopSalesByLocation(period, normalizeCriteria(criteria));
        } catch (err) {
            showErrorMessage(t('salesByLocation.error.fetch'));
            return { data: [], totalRecords: 0 };
        }
    };


    const fetchMaxTransactionCount = async () => {
        try {
            if (period) {
                const count = await TransactionService.getMaxTransactionCountForSalesByLocation(period);
                setMaxTransactionCount((!count || count < 25) ? 25 : count);
            }
        } catch (err) {
            showErrorMessage(t('salesByLocation.error.fetchMaxTransactionCount'));
        }
    }

    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    }

    const tableToolbarLeft = (
        <div className='flex align-items-center gap-2'>
            <LocationIcon />
            <span className='widget-name'>{t('salesByLocation.widgetName')}</span>
        </div>
    );

   const tableToolbarRight = (
        <SalesByLocationToolbar
            rowsPerPageOptions={rowsPerPageOptions}
            defaultSearchCriteria={defaultSearchCriteria}
            period={period}
            setPeriod={setPeriod}
            isCollapsed={isCollapsed}
            toggleCollapse={toggleCollapse}
        />
    );

    const growthTemplate = (row) => {
        const growth = row.growth;
        if (!growth) {
            return (
                <div className='growth-container'>
                    <span className='growth-value'>-</span>
                </div>
            );
        }
        let formattedGrowth = '';
        let color;
        if (growth > 0) {
            color = 'green';
            formattedGrowth = '+';
        } else if (growth < 0) {
            color = 'red';
        } else {
            color = 'gray';
        }
        formattedGrowth += FormattedUtils.formatPrice(growth) + '%';
        return (
            <div className='growth-container'>
                <span className={classNames('growth-value', color)}>{formattedGrowth}</span>
                {growth < 0 && (
                    <div className={classNames('status-colored', color)} style={{ width: 'fit-content' }}>
                        <div className={classNames('status-image', color)} />
                        <div className={classNames('status-name', color)}>{t('salesByLocation.salesDrop')}</div>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className={`top-sales-by-location-widget widget data-card ${isCollapsed ? 'collapsed' : ''}`}>
            <DataTable
                tableToolbarLeft={tableToolbarLeft}
                tableToolbarRight={tableToolbarRight}
                globalSearchPosition="none"
                fetchData={fetchData}
                filterConfig={filterConfig}
                defaultSearchCriteria={defaultSearchCriteria}
                showRowsPerPage={false}
                showPaginator={false}
                minWidth="800px"
            >
                <Column field="location" header={t('salesByLocation.location')} sortable sortField="location" id="location"
                    style={{ minWidth: '160px', maxWidth: '220px' }}
                />
                <Column field="city" header={t('salesByLocation.city')} sortable sortField="city" id="city"
                    style={{ minWidth: '120px', maxWidth: '160px' }}
                />
                <Column field="transactionCount" header={t('salesByLocation.transactionCount')} sortable sortField="transactionCount" id="transactionCount"
                    headerClassName="dt2-header-wrap"
                    style={{ minWidth: '130px', maxWidth: '150px' }}
                />
                <Column field="revenue" header={t('salesByLocation.revenue')} sortable sortField="revenue" id="revenue"
                    body={row => FormattedUtils.formatPrice(row.revenue)}
                    style={{ minWidth: '120px', maxWidth: '150px' }}
                />
                <Column field="growth" header={t('salesByLocation.growth')} sortable sortField="growth" id="growth"
                    body={growthTemplate}
                    style={{ minWidth: '130px', maxWidth: '180px' }}
                    headerClassName="dt2-header-wrap"
                />
            </DataTable>
        </div>
    )

}

export default SalesByLocationWidget;

